import { useEffect, useMemo, useRef, useState } from "react";
import AdminShell from "../../components/AdminShell";
import MilestoneStepper from "../../components/MilestoneStepper";
import { Button } from "../../components/ui/button";
import { adminApi } from "../../lib/api";
import {
  DROP_PERCENTS,
  FALLBACK_VIEWER_MILESTONES as DEFAULT_VIEWER_MILESTONES,
  computeMilestonePrices,
  priceForViewers,
} from "../../lib/priceEngine";
import { toast } from "sonner";

const LIVE_DURATION = 120;            // 2 minutes
const BUY_WINDOW = 15 * 60;           // 15 minutes

export default function AdminPriceEngine() {
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [mrp, setMrp] = useState(2999);
  const [startingPrice, setStartingPrice] = useState(2499);
  const [floorPrice, setFloorPrice] = useState(799);
  const [viewers, setViewers] = useState(0);
  const [auto, setAuto] = useState(false);
  const autoRef = useRef(null);
  const [cohort, setCohort] = useState(null); // { cohort_key, cohort_label, cohort_cap, active_audience, thresholds }

  // Phase state machine: idle → live (120s) → buy_window (15m) → ended
  const [phase, setPhase] = useState("idle");
  const [secondsLeft, setSecondsLeft] = useState(LIVE_DURATION);
  const [lockedPrice, setLockedPrice] = useState(null);
  const tickRef = useRef(null);

  useEffect(() => {
    adminApi.get("/products").then((r) => setProducts(r.data || []));
    adminApi.get("/price-engine/cohort").then((r) => setCohort(r.data)).catch(() => {});
  }, []);

  const thresholds = cohort?.thresholds || DEFAULT_VIEWER_MILESTONES;
  const maxViewer = Math.max(cohort?.cohort_cap || 250, thresholds[4] || 250);

  // When a product is selected, hydrate the 3 prices
  const applyProduct = (id) => {
    setProductId(id);
    const p = products.find((x) => x.product_id === id);
    if (!p) return;
    setMrp(Number(p.mrp || p.starting_price || p.base_price || 0));
    setStartingPrice(Number(p.starting_price || p.base_price || 0));
    setFloorPrice(Number(p.final_price || (p.base_price || 0) * 0.5));
  };

  // Pre-calculated milestone prices
  const milestonePrices = useMemo(() => computeMilestonePrices(startingPrice, floorPrice), [startingPrice, floorPrice]);
  const totalDiff = Math.max(0, startingPrice - floorPrice);
  const activeSellingPrice = lockedPrice ?? priceForViewers(viewers, startingPrice, milestonePrices, thresholds);
  const activeMilestoneIdx = (() => {
    for (let i = thresholds.length - 1; i >= 0; i--) {
      if (viewers >= thresholds[i]) return i;
    }
    return -1;
  })();

  // Tick the timer
  useEffect(() => {
    if (phase === "idle" || phase === "ended") return;
    tickRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          if (phase === "live") {
            // Lock the price, switch to buy_window
            setLockedPrice((cur) => cur ?? priceForViewers(viewers, startingPrice, milestonePrices, thresholds));
            setPhase("buy_window");
            return BUY_WINDOW;
          }
          if (phase === "buy_window") {
            setPhase("ended");
            return 0;
          }
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [phase, viewers, startingPrice, milestonePrices]);

  // Auto-simulate viewer growth: every 600ms, +(15–60) viewers until plateauing at random target
  useEffect(() => {
    if (!auto) { clearInterval(autoRef.current); return; }
    const target = Math.floor(maxViewer * 0.75) + Math.floor(Math.random() * (maxViewer * 0.2));
    autoRef.current = setInterval(() => {
      setViewers((v) => {
        if (v >= target) return v;
        const step = Math.max(1, Math.floor(maxViewer / 60));
        const next = Math.min(maxViewer, v + Math.floor(Math.random() * step) + Math.floor(step / 3));
        return next;
      });
    }, 600);
    return () => clearInterval(autoRef.current);
  }, [auto, maxViewer]);

  const startLive = () => {
    if (startingPrice <= 0 || floorPrice < 0 || floorPrice >= startingPrice) {
      toast.error("Set valid Starting > Floor first");
      return;
    }
    setLockedPrice(null);
    setSecondsLeft(LIVE_DURATION);
    setPhase("live");
    toast.success("2-min live timer started");
  };

  const resetSim = () => {
    clearInterval(tickRef.current);
    clearInterval(autoRef.current);
    setAuto(false);
    setPhase("idle");
    setSecondsLeft(LIVE_DURATION);
    setLockedPrice(null);
    setViewers(0);
  };

  return (
    <AdminShell>
      <div className="brand-tag">// PRICE ENGINE</div>
      <h1 className="font-display text-5xl uppercase tracking-tight mt-1">Crash Simulator</h1>
      <p className="mt-2 text-[#6E6E6E] text-sm max-w-2xl font-mono-ds">
        Test the live milestone pricing engine. Thresholds auto-scale with the currently-active cohort — as admin promotes more members, the crash points shift with them.
      </p>

      {/* Cohort banner */}
      {cohort && (
        <div className="mt-4 border-2 border-[#264733] bg-[#B9E641]/25 px-4 py-3 flex flex-wrap items-center justify-between gap-2" data-testid="cohort-banner">
          <div className="flex items-center gap-3">
            <span className="text-2xl leading-none">{cohort.cohort_emoji}</span>
            <div>
              <div className="brand-tiny text-[#264733] font-bold">// Active Cohort</div>
              <div className="font-display text-xl uppercase tracking-tight mt-0.5" data-testid="cohort-label">{cohort.cohort_label}</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div>
              <div className="brand-tiny text-[#6E6E6E]">Active Audience</div>
              <div className="font-display text-2xl tabular-nums text-[#161616]" data-testid="cohort-active">{cohort.active_audience.toLocaleString("en-IN")}</div>
            </div>
            <div>
              <div className="brand-tiny text-[#6E6E6E]">Reference Cap</div>
              <div className="font-display text-2xl tabular-nums text-[#161616]">{cohort.cohort_cap.toLocaleString("en-IN")}</div>
            </div>
            <div>
              <div className="brand-tiny text-[#6E6E6E]">Milestones</div>
              <div className="font-mono-ds text-sm text-[#264733] font-bold" data-testid="cohort-thresholds">{cohort.thresholds.join(" · ")}</div>
            </div>
          </div>
        </div>
      )}

      {/* Product chooser + price inputs */}
      <section className="mt-8 grid lg:grid-cols-[1fr_280px] gap-3">
        <div className="border border-[#E5E1D8] bg-[#FFFFFF] p-5">
          <div className="font-mono-ds text-[10px] uppercase tracking-[0.3em] text-[#6E6E6E] mb-3">Source product (optional)</div>
          <select
            value={productId}
            onChange={(e) => applyProduct(e.target.value)}
            data-testid="price-engine-product-select"
            className="w-full bg-[#FFFFFF] border border-[#E5E1D8] px-3 py-2 font-mono-ds text-sm focus:border-[#FF6A00] outline-none"
          >
            <option value="">— Manual override —</option>
            {products.map((p) => (
              <option key={p.product_id} value={p.product_id}>{`${p.name} (₹${p.starting_price || p.base_price})`}</option>
            ))}
          </select>
          <div className="grid grid-cols-3 gap-3 mt-4">
            <PriceInput label="MRP" value={mrp} onChange={setMrp} testId="pe-mrp" />
            <PriceInput label="Starting" value={startingPrice} onChange={setStartingPrice} testId="pe-starting" accent />
            <PriceInput label="Floor" value={floorPrice} onChange={setFloorPrice} testId="pe-floor" />
          </div>
          <div className="mt-3 font-mono-ds text-[10px] uppercase tracking-widest text-[#6E6E6E]">
            Total drop range: <span className="text-[#161616]">₹{totalDiff.toLocaleString("en-IN")}</span>
          </div>
        </div>

        <div className="border border-[#E5E1D8] bg-[#FFFFFF] p-5 flex flex-col gap-2">
          <div className="font-mono-ds text-[10px] uppercase tracking-[0.3em] text-[#6E6E6E]">Engine Controls</div>
          {phase === "idle" || phase === "ended" ? (
            <Button onClick={startLive} className="rounded-2xl bg-[#FF6A00] hover:bg-[#FFFFFF] hover:text-[#161616] text-[#161616] font-mono-ds uppercase tracking-widest h-11" data-testid="start-sim-btn">
              ▶ Start 2-min Live
            </Button>
          ) : (
            <Button onClick={resetSim} variant="outline" className="rounded-2xl border-[#E5E1D8] hover:border-[#FF6A00] hover:text-[#FF6A00] bg-transparent text-[#161616] font-mono-ds uppercase tracking-widest h-11" data-testid="reset-sim-btn">
              ■ Reset
            </Button>
          )}
          <label className="flex items-center justify-between gap-2 mt-1 font-mono-ds text-xs uppercase tracking-widest text-[#6E6E6E] cursor-pointer">
            <span>Auto-simulate viewers</span>
            <input
              type="checkbox"
              checked={auto}
              onChange={(e) => setAuto(e.target.checked)}
              className="accent-[#FF6A00] w-4 h-4"
              data-testid="auto-sim-toggle"
            />
          </label>
        </div>
      </section>

      {/* Live tracker grid */}
      <section className="mt-3 border border-[#E5E1D8] bg-[#FFFFFF]">
        <div className="grid md:grid-cols-[1fr_1fr_1fr] gap-px bg-[#FFFFFF]">
          {/* Timer */}
          <TimerTile phase={phase} secondsLeft={secondsLeft} />

          {/* Live price */}
          <PriceTile
            phase={phase}
            price={activeSellingPrice}
            mrp={mrp}
            locked={!!lockedPrice}
            milestoneIdx={activeMilestoneIdx}
          />

          {/* Viewers */}
          <ViewersTile viewers={viewers} milestoneIdx={activeMilestoneIdx} thresholds={thresholds} />
        </div>

        {/* Stepper bar */}
        <div className="p-5 border-t border-[#FFFFFF]">
          <MilestoneStepper
            milestones={thresholds.map((t, i) => ({ threshold: t, price: milestonePrices[i] }))}
            viewers={viewers}
            title={cohort ? `Milestone Stepper · ${cohort.cohort_emoji || ""} ${cohort.cohort_label || "Founding Members"}` : "Milestone Stepper"}
          />
        </div>

        {/* Viewer slider */}
        <div className="p-5 border-t border-[#FFFFFF]">
          <div className="flex items-end justify-between mb-2">
            <label className="font-mono-ds text-[10px] uppercase tracking-[0.3em] text-[#6E6E6E]">
              Concurrent Viewers (drag to simulate)
            </label>
            <span className="font-display text-2xl text-[#161616] tabular-nums">{viewers.toLocaleString("en-IN")}</span>
          </div>
          <input
            type="range"
            min={0}
            max={maxViewer}
            step={1}
            value={viewers}
            onChange={(e) => setViewers(parseInt(e.target.value, 10))}
            disabled={phase === "ended"}
            className="w-full accent-[#FF6A00] cursor-pointer disabled:opacity-50"
            data-testid="viewer-slider"
          />
          <div className="flex justify-between font-mono-ds text-[9px] uppercase tracking-widest text-[#6E6E6E] mt-1">
            <span>0</span>
            {thresholds.map((t) => <span key={t}>{t.toLocaleString("en-IN")}</span>)}
            <span>{maxViewer.toLocaleString("en-IN")}</span>
          </div>
        </div>

        {/* Buy Now CTA appears during buy_window */}
        {phase === "buy_window" && (
          <div className="p-6 border-t border-[#FFFFFF] bg-gradient-to-r from-[#FF6A00]/10 to-transparent">
            <BuyNowCTA price={lockedPrice} secondsLeft={secondsLeft} />
          </div>
        )}

        {phase === "ended" && (
          <div className="p-6 border-t border-[#FFFFFF] text-center">
            <div className="font-mono-ds text-[10px] uppercase tracking-[0.4em] text-[#6E6E6E]">// Drop closed</div>
            <div className="font-display text-3xl uppercase tracking-tight mt-2">Buy window expired</div>
            <div className="mt-2 font-mono-ds text-xs text-[#6E6E6E]">Final lock-in price was <span className="text-[#FF6A00] font-bold">₹{lockedPrice?.toLocaleString("en-IN")}</span></div>
          </div>
        )}
      </section>

      {/* Milestone breakdown table */}
      <section className="mt-3 border border-[#E5E1D8] bg-[#FFFFFF] p-5">
        <div className="font-mono-ds text-[10px] uppercase tracking-[0.3em] text-[#6E6E6E] mb-3">Pre-calculated Milestone Prices</div>
        <table className="w-full text-sm font-mono-ds">
          <thead className="text-[10px] uppercase tracking-[0.3em] text-[#6E6E6E]">
            <tr className="border-b border-[#E5E1D8]">
              <th className="text-left p-2">Tier</th>
              <th className="text-left p-2">Threshold</th>
              <th className="text-left p-2">Drop %</th>
              <th className="text-left p-2">Price</th>
              <th className="text-left p-2">Δ from Starting</th>
            </tr>
          </thead>
          <tbody>
            {milestonePrices.map((p, i) => {
              const active = activeMilestoneIdx === i;
              return (
                <tr key={`row-${thresholds[i]}`} className={`border-b border-[#FFFFFF] ${active ? "bg-[#FF6A00]/10" : ""}`} data-testid={`milestone-row-${i + 1}`}>
                  <td className={`p-2 ${active ? "text-[#FF6A00] font-bold" : "text-[#161616]"}`}>M{i + 1}</td>
                  <td className="p-2 text-[#6E6E6E]">≥ {thresholds[i].toLocaleString("en-IN")}</td>
                  <td className="p-2 text-[#6E6E6E]">{Math.round(DROP_PERCENTS[i] * 100)}% of (starting − floor)</td>
                  <td className={`p-2 ${active ? "text-[#FF6A00] font-bold" : "text-[#161616]"}`}>₹{p.toLocaleString("en-IN")}</td>
                  <td className="p-2 text-[#6E6E6E]">−₹{(startingPrice - p).toLocaleString("en-IN")}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </AdminShell>
  );
}

/* ---------- Sub-components ---------- */
function PriceInput({ label, value, onChange, testId, accent }) {
  return (
    <div>
      <label className={`font-mono-ds text-[9px] uppercase tracking-[0.3em] ${accent ? "text-[#FF6A00]" : "text-[#6E6E6E]"}`}>{label}</label>
      <input
        type="number"
        min="0"
        step="0.01"
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className="w-full mt-1 bg-transparent border border-[#E5E1D8] px-3 py-2 font-mono-ds text-sm focus:border-[#FF6A00] outline-none"
        data-testid={testId}
      />
    </div>
  );
}

function TimerTile({ phase, secondsLeft }) {
  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;
  const label = phase === "live" ? "Live ends in" :
                phase === "buy_window" ? "Buy window closes in" :
                phase === "ended" ? "Closed" : "Idle";
  const isLive = phase === "live";
  return (
    <div className="p-6 bg-[#FFFFFF]">
      <div className="font-mono-ds text-[9px] uppercase tracking-[0.4em] text-[#6E6E6E]">{label}</div>
      <div
        className={`font-display text-6xl tracking-tighter tabular-nums mt-2 ${isLive ? "text-[#FF6A00]" : phase === "buy_window" ? "text-[#161616]" : "text-[#6E6E6E]"}`}
        data-testid="timer-display"
      >
        {String(mins).padStart(2, "0")}:{String(secs).padStart(2, "0")}
      </div>
      <div className="font-mono-ds text-[10px] uppercase tracking-widest text-[#6E6E6E] mt-2">
        Phase: <span className={phase === "live" ? "text-[#FF6A00]" : "text-[#161616]"}>{phase.toUpperCase()}</span>
      </div>
    </div>
  );
}

function PriceTile({ phase, price, mrp, locked, milestoneIdx }) {
  const tierLabel = milestoneIdx < 0 ? "PRE-TIER" : `TIER ${milestoneIdx + 1}/5`;
  const isLive = phase === "live";
  const isLocked = phase === "buy_window" || phase === "ended";
  return (
    <div className="p-6 bg-[#FFFFFF] relative overflow-hidden">
      <div className="font-mono-ds text-[9px] uppercase tracking-[0.4em] text-[#6E6E6E]">
        {isLocked ? "Locked Price" : "Live Price"} · {tierLabel}
      </div>
      <div className="flex items-baseline gap-3 mt-2">
        <span
          className={`font-display text-6xl tracking-tighter tabular-nums leading-none text-[#FF6A00] ${isLive ? "pulse-dot" : ""}`}
          data-testid="active-selling-price"
          style={isLive ? { animation: "pulse-dot 1.5s ease-in-out infinite" } : {}}
        >
          ₹{Number(price || 0).toLocaleString("en-IN")}
        </span>
      </div>
      {mrp > price && (
        <div className="font-mono-ds text-sm text-[#6E6E6E] line-through mt-1">MRP ₹{Number(mrp).toLocaleString("en-IN")}</div>
      )}
      {locked && <div className="mt-2 font-mono-ds text-[10px] uppercase tracking-widest text-[#FF6A00]">◆ Frozen — checkout open</div>}
    </div>
  );
}

function ViewersTile({ viewers, milestoneIdx, thresholds }) {
  return (
    <div className="p-6 bg-[#FFFFFF]">
      <div className="font-mono-ds text-[9px] uppercase tracking-[0.4em] text-[#6E6E6E]">Concurrent Viewers</div>
      <div className="font-display text-6xl tracking-tighter tabular-nums mt-2 text-[#161616]" data-testid="viewers-display">
        {viewers.toLocaleString("en-IN")}
      </div>
      <div className="font-mono-ds text-[10px] uppercase tracking-widest text-[#6E6E6E] mt-2">
        {milestoneIdx < 0 ? "Below first threshold" : `Threshold ${thresholds[milestoneIdx]}+ unlocked`}
      </div>
    </div>
  );
}

function Stepper(_unused) { return null; /* superseded by shared MilestoneStepper */ }

function BuyNowCTA({ price, secondsLeft }) {
  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;
  return (
    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
      <div>
        <div className="brand-tag">◆ Price Frozen</div>
        <div className="font-display text-4xl uppercase tracking-tight mt-1">Buy Now @ <span className="text-[#FF6A00]">₹{Number(price || 0).toLocaleString("en-IN")}</span></div>
        <div className="font-mono-ds text-xs text-[#6E6E6E] mt-1 uppercase tracking-widest">
          {String(mins).padStart(2, "0")}:{String(secs).padStart(2, "0")} left in buy window
        </div>
      </div>
      <button
        className="bg-[#FF6A00] hover:bg-[#FFFFFF] hover:text-[#161616] text-[#161616] font-mono-ds uppercase tracking-widest text-base px-10 h-16 font-bold animate-pulse hover:animate-none shadow-[0_4px_12px_rgba(17,17,17,0.06)]"
        data-testid="checkout-cta"
      >
        ▸ Checkout
      </button>
    </div>
  );
}
